import React from "react";
import styled, { css } from "styled-components";
import {
    colorTen,
    hoverLinkBlue,
    colorTwoBright,
    colorTwoDark,
    lightBorderGray,
    altGrayTextColor,
    activePsychSort,
    activeSortDirection,
    inactiveSortDirection,
} from "../../../globalStyles/colors/scoutColors";
import { device } from "../../../globalStyles/breakpoints";
import { FcGenericSortingDesc } from "@react-icons/all-files/fc/FcGenericSortingDesc";
import { FcGenericSortingAsc } from "@react-icons/all-files/fc/FcGenericSortingAsc";
import { RiCake2Line } from "@react-icons/all-files/ri/RiCake2Line";
import { BiBuildingHouse } from "@react-icons/all-files/bi/BiBuildingHouse";
import { BiHomeHeart } from "@react-icons/all-files/bi/BiHomeHeart";
import { FaRegMoneyBillAlt } from "@react-icons/all-files/fa/FaRegMoneyBillAlt";
import { GrGroup } from "@react-icons/all-files/gr/GrGroup";
import { FiChevronDown } from "@react-icons/all-files/fi/FiChevronDown";
import { MdChildCare } from "@react-icons/all-files/md/MdChildCare";

//  ██████╗ ███████╗███╗   ██╗███████╗██████╗  █████╗ ██╗         ███████╗ ██████╗
// ██╔════╝ ██╔════╝████╗  ██║██╔════╝██╔══██╗██╔══██╗██║         ██╔════╝██╔════╝
// ██║  ███╗█████╗  ██╔██╗ ██║█████╗  ██████╔╝███████║██║         ███████╗██║
// ██║   ██║██╔══╝  ██║╚██╗██║██╔══╝  ██╔══██╗██╔══██║██║         ╚════██║██║
// ╚██████╔╝███████╗██║ ╚████║███████╗██║  ██║██║  ██║███████╗    ███████║╚██████╗
//  ╚═════╝ ╚══════╝╚═╝  ╚═══╝╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝    ╚══════╝ ╚═════╝
// (GENERAL STYLED COMPONENTS)

const colorTransitionTime = "0.3s";
const visibilityTransitionTime = "0.5s";

interface ISegCirc {
  color: string;
}

export const SegContainer = styled.div`
  display: grid;
  grid-template-columns:
    minmax(200px, 440px) minmax(120px, 280px) 20px minmax(185px, 280px)
    minmax(180px, 250px);
  place-items: stretch;
  ${device.desktop} {
    grid-template-columns:
      minmax(250px, 440px) minmax(120px, 280px) 20px minmax(185px, 280px)
      minmax(180px, 250px);
  }
`;

export const SegCell = styled.div`
  text-align: center;
  width: 100%;
  padding: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: relative;
  transition: border-color ${colorTransitionTime};
  &.hide-show {
    > .animated:last-child,
    > .animated:first-child {
      transition: opacity ${visibilityTransitionTime},
        transform ${visibilityTransitionTime};
    }
    > .animated:last-child {
      transform: translateY(50px) scale(0.5);
      opacity: 0;
    }
  }
  &.border-left {
    border-left: 1px solid ${colorTen};
    justify-content: space-between;
    > span {
      width: 70px;
    }
  }
  ${device.desktop} {
    padding: 25px 12px;
    &.fill {
      padding: 0;
      > div {
        width: 1px;
        height: 100%;
        border-right: 1px solid ${colorTen};
      }
    }
  }
`;

export const SegCellTextL = styled.div`
  color: ${altGrayTextColor};
  font-size: 20px;
  font-weight: 800;
  line-height: 17px;
`;

export const SegCellTextSm = styled.div`
  color: ${altGrayTextColor};
  font-size: 13px;
  font-weight: 400;
  line-height: 17px;
`;

export const SegCellTextSmB = styled(SegCellTextSm)`
  color: ${altGrayTextColor};
  font-weight: 800;
`;

export const SegContentSecHeader = styled.h4`
  color: ${colorTwoBright};
  font-size: 18px;
`;

export const SegImg = styled.img`
  width: 8vw;
  height: 8vw;
  max-width: 90px;
  max-height: 90px;
  min-width: 65px;
  min-height: 65px;
  display: inline-block;
  border-radius: 50%;
  object-fit: cover;
  object-position: center;
`;

export const SegCardHead = styled.div`
  display: flex;
  align-items: center;
  padding: 0 15px;
  margin-bottom: 32px;
  ${SegImg} {
    margin-right: 22px;
  }
`;

export const SegCardRow = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  background-color: #f5f5f5;
  height: 37px;
  margin: 2px 13px 11.2px 8px;
  padding: 1px 0;
  border-radius: 13px;
`;

export const SegCardCol = styled.span`
  display: inline-block;
  > svg {
    margin-right: 17px;
  }
  &:nth-child(1) {
    width: 230px;
    display: flex;
    align-items: center;
  }
  &:nth-child(2) {
    width: 150px;
    text-align: center;
  }
  &:nth-child(4),
  &:nth-child(6) {
    width: 55px;
    text-align: center;
  }
`;

export const SegCardSpacer = styled.span`
  width: 1px;
  height: 35px;
  border-left: 1px solid ${lightBorderGray};
`;

export const SegCardHeaderCol = styled(SegCardCol)``;

export const SegCardHeaderRow = styled(SegCardRow)`
  font-size: 9px;
  color: #6d7278;
  height: 11px;
  background-color: transparent;
  ${SegCardSpacer} {
    border-color: transparent;
  }
`;

export const SegCardBreakoutContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin: 30px auto 18px;
  max-width: 90%;
  align-items: center;

  ${device.desktop} {
    justify-content: space-between;
  }
`;

export const SegCardBreakoutItem = styled.div`
  text-align: center;
  max-width: 27%;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.85);
  &:last-child {
    color: ${altGrayTextColor};
  }
  > span {
    font-weight: 800;
  }
`;

export const SegCardBreakoutVerticalBreak = styled.div`
  border-right: 1px solid ${lightBorderGray};
  width: 1px;
  height: 54px;
  margin: 0 7px;
`;

export const SegFooterSubText = styled.p`
  font-size: 9px;
  color: #8c8c8c;
`;

const SegHeadStyle = css`
  margin: 50px 7px 0 1px;
`;

const SegHeadFirstChildStyle = css`
  padding-top: 0;
  &:first-child {
    flex-direction: row;
    justify-content: left;
    padding-left: 0;
  }
`;

const SegBodyStyle = css`
  max-height: 665px;
  overflow-y: auto;
  overflow-x: hidden;
  ${SegCell} {
    border-bottom: 1px solid ${colorTen};
    &:first-child {
      padding-left: 17px;
    }
  }
`;

export const SegDetails = styled.div`
  text-align: left;
  margin-left: 25px;
  line-height: normal;
`;

export const SegLink = styled.div`
  display: flex;
  align-items: center;
  text-decoration: underline;
  transition: color ${colorTransitionTime};
  &:hover {
    color: ${hoverLinkBlue};
  }
`;

export const SegCirc = styled.div`
  width: 94px;
  height: 94px;
  border-radius: 50%;
  border: 4px solid ${(props: ISegCirc) => props.color};
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const SegCircTextL = styled.div`
  font-size: 17px;
  font-weight: 800;
  line-height: normal;
`;

export const SegCircTextSm = styled.div`
  font-size: 11px;
  font-weight: 300;
  line-height: normal;
`;

// Top Right interaction inside of a cell that will be used only on tablet
export const SegCellTRAction = styled.div`
  position: absolute;
  top: -9px;
  right: -9px;
  width: 48px;
  height: 48px;
  cursor: pointer;
  color: #8c8c8c;
  display: flex;
  align-items: center;
  justify-content: center;
  ${device.desktop} {
    display: none;
  }
`;

export const EmptyResultsCell = styled(SegCell)`
  flex-direction: column;
  padding: 50px;
  color: ${lightBorderGray};
`;

// ███╗   ██╗ ██████╗ ███╗   ██╗      ███████╗ ██████╗ ██████╗ ████████╗
// ████╗  ██║██╔═══██╗████╗  ██║      ██╔════╝██╔═══██╗██╔══██╗╚══██╔══╝
// ██╔██╗ ██║██║   ██║██╔██╗ ██║█████╗███████╗██║   ██║██████╔╝   ██║
// ██║╚██╗██║██║   ██║██║╚██╗██║╚════╝╚════██║██║   ██║██╔══██╗   ██║
// ██║ ╚████║╚██████╔╝██║ ╚████║      ███████║╚██████╔╝██║  ██║   ██║
// ╚═╝  ╚═══╝ ╚═════╝ ╚═╝  ╚═══╝      ╚══════╝ ╚═════╝ ╚═╝  ╚═╝   ╚═╝

export const SegRow = styled.div`
  display: contents;
  &.active {
    .hide-show {
      .animated:first-child {
        transform: translateY(-50px) scale(0.5);
        opacity: 0;
      }
      .animated:last-child {
        transform: translateY(0);
        opacity: 1;
      }
    }
  }
  > ${SegCell} {
    &:first-child {
      ${device.desktop} {
        flex-direction: row;
      }
    }
    &:last-child {
      border-left: 1px solid ${colorTen};
    }
  }
`;

export const SegBody = styled(SegContainer)`
  ${SegBodyStyle}
  border: 1px solid ${colorTen};
  ${SegRow} {
    &:last-child {
      ${SegCell} {
        border-bottom: 0;
      }
    }
  }
`;

export const SegHead = styled(SegContainer)`
  ${SegHeadStyle}
  &.too-short {
    /* ERY: again, I hate windows :-P */
    margin-right: 1px;
  }
  ${SegCell} {
    ${SegHeadFirstChildStyle}
  }
`;

// ███████╗ ██████╗ ██████╗ ████████╗
// ██╔════╝██╔═══██╗██╔══██╗╚══██╔══╝
// ███████╗██║   ██║██████╔╝   ██║
// ╚════██║██║   ██║██╔══██╗   ██║
// ███████║╚██████╔╝██║  ██║   ██║
// ╚══════╝ ╚═════╝ ╚═╝  ╚═╝   ╚═╝

export const SortSegCellWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  width: 100%;
  align-items: stretch;
`;

export const SortSegRow = styled.div`
  display: flex;
  ${SegCell} {
    &:first-child {
      ${device.desktop} {
        flex-direction: row;
      }
    }
  }
`;

export const SortSegHead = styled.div`
  ${SegHeadStyle}
  margin-bottom: 7px;
  ${SortSegCellWrapper}:first-child {
    ${SegCell} {
      ${SegHeadFirstChildStyle}
      padding-bottom: 0;
    }
  }
`;

const SortSegBodyStyle = css`
  max-height: 665px;
  ${SortSegCellWrapper} {
    border-left: 1px solid ${colorTen};
    border-right: 1px solid ${colorTen};
    transition: border-color ${colorTransitionTime};
    &:first-child {
      border-right: 0;
      ${SegCell} {
        justify-content: left;
        padding-left: 17px;
      }
    }
    &.polygonOneActive {
      border-left-color: ${colorTwoBright};
      border-right-color: ${colorTwoBright};
    }
    &.polygonTwoActive {
      border-left-color: ${colorTwoDark};
      border-right-color: ${colorTwoDark};
    }
    ${SegCell} {
      border-bottom: 1px solid ${colorTen};
    }
  }
`;

export const SortSegBody = styled.div`
  ${SortSegBodyStyle}
  overflow-y: auto;
  ${SortSegRow} {
    &:last-child {
      ${SortSegCellWrapper} {
        ${SegCell} {
          border-bottom: 0;
        }
      }
    }
  }
`;

export const SortSegFooter = styled.div`
  height: 1px;
  overflow: hidden;
  &.too-tall {
    width: calc(
      100% - 6px
    ); /* ERY: because windows is dumb and doesn't auto hide it's scrollbars */
  }
  ${SortSegCellWrapper} {
    height: 1px;
    border-left: 1px solid ${colorTen};
    border-right: 1px solid ${colorTen};
    background-color: ${colorTen};
    transition: background-color ${colorTransitionTime},
      border-color ${colorTransitionTime};
    &:first-child {
      border-right: 0;
    }
    &.active {
      background-color: ${activePsychSort};
      border-color: ${activePsychSort};
    }
  }
`;

export const SortSegHeadActions = styled.div`
  ${SortSegBodyStyle}
  &.too-tall {
    width: calc(
      100% - 6px
    ); /* ERY: because windows is dumb and doesn't auto hide it's scrollbars */
  }
  .ant-radio-wrapper,
  .ant-radio-checked .ant-radio-inner {
    transition: color ${colorTransitionTime},
      background-color ${colorTransitionTime},
      border-color ${colorTransitionTime};
  }
  .ant-radio-wrapper:hover .ant-radio,
  .ant-radio:hover .ant-radio-inner,
  .ant-radio-input:focus + .ant-radio-inner,
  .ant-radio-checked .ant-radio-inner {
    border-color: ${activePsychSort};
  }
  .ant-radio-inner:after {
    background-color: ${activePsychSort};
  }
  span.ant-radio + * {
    padding-right: 0;
  }
  ${SortSegCellWrapper} {
    border-top: 1px solid ${lightBorderGray};
    transition: color ${colorTransitionTime},
      background-color ${colorTransitionTime},
      border-color ${colorTransitionTime};
    &.active {
      background-color: ${activePsychSort};
      color: white;
      border-top-color: ${activePsychSort};
      .ant-radio-wrapper {
        color: white;
      }
      ${SegCell} {
        border-color: ${activePsychSort};
      }
    }
    ${SegCell} {
      padding: 7px;
      flex-direction: row;
      > .single-case-title {
        width: 70px;
      }
    }
    &:first-child {
      ${SegCell} {
        font-weight: 500;
        ${device.upToDesktop} {
          justify-content: center;
        }
      }
    }
  }
`;

export const SortSegCardBreakoutContainer = styled(SegCardBreakoutContainer)`
  justify-content: space-around;
`;

// ██╗ ██████╗ ██████╗ ███╗   ██╗███████╗
// ██║██╔════╝██╔═══██╗████╗  ██║██╔════╝
// ██║██║     ██║   ██║██╔██╗ ██║███████╗
// ██║██║     ██║   ██║██║╚██╗██║╚════██║
// ██║╚██████╗╚██████╔╝██║ ╚████║███████║
// ╚═╝ ╚═════╝ ╚═════╝ ╚═╝  ╚═══╝╚══════╝

const AscDescIconStyles = css`
  margin-right: 5px;
  rect {
    fill: ${inactiveSortDirection};
    transition: fill ${colorTransitionTime};
  }
  &.active {
    rect {
      fill: ${activeSortDirection};
    }
  }
`;

export const AscIcon = styled(FcGenericSortingAsc)`
  ${AscDescIconStyles}
`;

export const DescIcon = styled(FcGenericSortingDesc)`
  ${AscDescIconStyles}
`;

export const ChevronDown = styled(FiChevronDown)`
  color: #cecece;
  margin-left: auto;
`;

export const findSegIcon = (cat: string): JSX.Element => {
    switch (cat) {
        case "Age of children":
        case "Children":
            return <MdChildCare size={17} />;
        case "Estimated household income":
            return <FaRegMoneyBillAlt size={17} />;
        case "Head of household age":
            return <RiCake2Line size={17} />;
        case "Home ownership":
            return <BiHomeHeart size={17} />;
        case "Household size":
            return <GrGroup size={17} />;
        case "Type of property":
            return <BiBuildingHouse size={17} />;
        default:
            return null;
    }
};
